import { getRiskCategoryLabel } from '../../constants/riskCategories';

/**
 * Map SOX control `assertions` to RCM columns (C, E&O, A, V, R&O).
 */
export function financialAssertionsFromControlAssertions(assertions) {
  const raw = Array.isArray(assertions) ? assertions : [];
  const norm = new Set(raw.map((a) => String(a).trim().toLowerCase()));
  const hasExistence = norm.has('existence') || norm.has('occurence');
  const hasAccuracyValuation = norm.has('accuracy & valuation');
  return {
    completeness: norm.has('completeness') ? 'Yes' : 'No',
    existenceOccurrence: hasExistence ? 'Yes' : 'No',
    accuracy: hasAccuracyValuation ? 'Yes' : 'No',
    valuation: hasAccuracyValuation ? 'Yes' : 'No',
    rightsObligations: norm.has('rights & obligation') ? 'Yes' : 'No',
  };
}

export function formatRcmClassificationLabel(raw) {
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'itgc') return 'ITGC';
  if (value === 'business') return 'Business';
  return value ? String(raw).trim() : '-';
}

/** RCM risk category from API `risk_category`; defaults to SOX when empty. */
export function formatRcmRiskCategory(raw) {
  const s = raw == null || raw === '' ? '' : String(raw).trim();
  if (!s) return 'SOX';
  return getRiskCategoryLabel(s);
}

export function formatRcmKeyReportsLabel(reports) {
  if (!Array.isArray(reports) || reports.length === 0) return '';
  return reports
    .map((item) => {
      if (!item || typeof item !== 'object') return '';
      return String(item.name ?? '').trim();
    })
    .filter(Boolean)
    .join(', ');
}

/** Split a key-reports cell value (array or comma-separated label) into names. */
export function parseRcmKeyReportNames(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object') return String(item.name ?? '').trim();
        return String(item ?? '').trim();
      })
      .filter(Boolean);
  }
  const text = value == null ? '' : String(value).trim();
  if (!text || text === '-') return [];
  return text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Compact Key Reports cell: first 3 names, then "+N more".
 * fullLabel is always the complete comma-separated list (for title/tooltip).
 */
export function formatRcmKeyReportsPreview(value, maxVisible = 3) {
  const names = parseRcmKeyReportNames(value);
  if (names.length === 0) {
    return { display: '-', fullLabel: '', isCompact: false };
  }
  const fullLabel = names.join(', ');
  if (names.length <= maxVisible) {
    return { display: fullLabel, fullLabel, isCompact: false };
  }
  const visible = names.slice(0, maxVisible).join(', ');
  const moreCount = names.length - maxVisible;
  return {
    display: `${visible} +${moreCount} more`,
    fullLabel,
    isCompact: true,
  };
}

export function mapApiItemToRcmRow(item, srNo) {
  const risk = item.risk || {};
  const proc = item.process || {};
  const sc = item.sox_control || {};
  const controlTypeRaw = sc.control_type;
  const controlType =
    controlTypeRaw === 'manual'
      ? 'Manual'
      : controlTypeRaw === 'automated'
        ? 'Automated'
        : controlTypeRaw || 'Manual';

  const fin = financialAssertionsFromControlAssertions(sc.assertions);
  const controlOwner = String(sc.control_owner ?? sc.controlOwner ?? '').trim();
  const controlPerformer = String(sc.control_performer ?? sc.controlPerformer ?? '').trim();
  const keyReportsLabel = formatRcmKeyReportsLabel(sc.key_reports ?? sc.keyReports);

  const defaultRow = {
    id: risk.id,
    srNo,
    classification: formatRcmClassificationLabel(item.classification || 'business'),
    soxCycle: proc.sox_cycle?.name || '',
    subProcess: proc.name || '',
    riskId: risk.risk_id || '',
    clientRiskId: risk.client_risk_id || risk.clientRiskId || '',
    riskDescription: risk.risk_description || '',
    controlObjective: sc.objective || '',
    controlDescription: sc.control_wording_text || sc.title || '',
    completeness: fin.completeness,
    existenceOccurrence: fin.existenceOccurrence,
    accuracy: fin.accuracy,
    valuation: fin.valuation,
    rightsObligations: fin.rightsObligations,
    what: sc.title || '',
    who: controlOwner || controlPerformer || '',
    keyReports: keyReportsLabel,
    when: sc.frequency || '',
    controlType,
    keyNonKey: sc.significance === 'key' ? 'Key' : 'Non-Key',
    riskCategory: formatRcmRiskCategory(risk.risk_category),
    mappingId: item.id,
    controlId: sc.control_id || '',
    processId: item.process_id,
    nodeId: item.node_id ?? '',
    riskUUID: risk.id,
  };

  const rawOverrides = sc.rcm_overrides ?? item.rcm_overrides;
  const overrides =
    rawOverrides && typeof rawOverrides === 'object' && !Array.isArray(rawOverrides) ? rawOverrides : {};
  const overrideableFields = [
    'riskDescription', 'controlObjective', 'controlDescription',
    'completeness', 'existenceOccurrence', 'accuracy', 'valuation', 'rightsObligations',
    'what', 'who', 'when', 'controlType', 'keyNonKey',
  ];
  for (const field of overrideableFields) {
    if (overrides[field] !== undefined) defaultRow[field] = overrides[field];
  }
  return defaultRow;
}

function formatItgcControlType(raw) {
  if (!raw) return '';
  const v = String(raw).toLowerCase();
  if (v === 'manual') return 'Manual';
  if (v === 'automated') return 'Automated';
  return raw;
}

function dashIfEmpty(value) {
  const text = value == null ? '' : String(value).trim();
  return text === '' ? '-' : text;
}

export function mapItgcApiItemToAllRow(item, srNo) {
  const isKey = (item.significance || '').toLowerCase() === 'key';
  const fin = financialAssertionsFromControlAssertions(item.assertions);
  return {
    rowType: 'ITGC',
    classification: formatRcmClassificationLabel(item.classification || 'itgc'),
    srNo,
    soxCycle: '-',
    subProcess: '-',
    domain: dashIfEmpty(item.domainCode),
    system: dashIfEmpty(item.systemName),
    riskId: dashIfEmpty(item.riskId),
    clientRiskId: dashIfEmpty(item.clientControlId),
    riskDescription: dashIfEmpty(item.riskDescription),
    controlId: dashIfEmpty(item.controlId),
    controlObjective: dashIfEmpty(item.controlObjective),
    controlDescription: dashIfEmpty(item.controlDescription),
    completeness: fin.completeness,
    existenceOccurrence: fin.existenceOccurrence,
    accuracy: fin.accuracy,
    valuation: fin.valuation,
    rightsObligations: fin.rightsObligations,
    what: dashIfEmpty(item.controlTitle),
    who: dashIfEmpty(item.controlOwner),
    keyReports: dashIfEmpty(formatRcmKeyReportsLabel(item.keyReports ?? item.key_reports)),
    when: dashIfEmpty(item.frequency),
    controlType: dashIfEmpty(formatItgcControlType(item.controlType)),
    keyNonKey: isKey ? 'Key' : 'Non-Key',
    riskCategory: formatRcmRiskCategory(item.riskCategory),
    sourceId: item.id,
  };
}

export function mapProcessApiItemToAllRow(item, srNo) {
  const row = mapApiItemToRcmRow(item, srNo);
  return {
    ...row,
    rowType: 'Process',
    classification: formatRcmClassificationLabel(item.classification || row.classification || 'business'),
    domain: '-',
    system: '-',
    soxCycle: dashIfEmpty(row.soxCycle),
    subProcess: dashIfEmpty(row.subProcess),
    riskId: dashIfEmpty(row.riskId),
    clientRiskId: dashIfEmpty(row.clientRiskId),
    riskDescription: dashIfEmpty(row.riskDescription),
    controlId: dashIfEmpty(row.controlId),
    controlObjective: dashIfEmpty(row.controlObjective),
    controlDescription: dashIfEmpty(row.controlDescription),
    what: dashIfEmpty(row.what),
    who: dashIfEmpty(row.who),
    keyReports: dashIfEmpty(row.keyReports),
    when: dashIfEmpty(row.when),
    controlType: dashIfEmpty(row.controlType),
    keyNonKey: dashIfEmpty(row.keyNonKey),
    riskCategory: dashIfEmpty(row.riskCategory),
    sourceId: item.id,
  };
}

export function mapAllApiItemToRow(item, srNo) {
  if (item?.rowType === 'itgc' && item.itgc) {
    return mapItgcApiItemToAllRow(item.itgc, srNo);
  }
  if (item?.rowType === 'process' && item.process) {
    return mapProcessApiItemToAllRow(item.process, srNo);
  }
  return null;
}
